import React, { useEffect, useState } from "react";
import Card from "./components/Card";
import { VscChromeClose } from "react-icons/vsc";
import { Col, Container, Form, Row } from "react-bootstrap";
import Category from "./components/Category";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import RangeSlider from "react-bootstrap-range-slider";
import KidService from "../../services/kids";
import CategoryService from "../../services/categories";
import Loader from "../../components/Loader";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow } from "swiper/modules";
import KidProfileModal from "./components/KidProfileModal";
import KidSponsorModal from "./components/KidSponsorModal";
import { FaChevronDown, FaChevronUp, FaSearch } from "react-icons/fa";
import MobileFooter from "../../components/MobileFooter";
import { useSponsorResumeReopen } from "../../utils/sponsorResume";
import { filterKidsForSponsorListing } from "../../utils/kidAvailability";

const SponsorKids = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [states, setStates] = useState([]);
  const [stateVal, setStateVal] = useState("");
  const [genderVal, setGenderVal] = useState("");
  const [budgetVal, setBudgetVal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(""); // New search state
  const [categories, setCategories] = useState([]);
  const [filterSponserKidsData, setFilterSponserKidsData] = useState([]);
  const [categoryId, setCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFilter, setOpenFilter] = useState(null); // 'gender' | 'state' | 'budget' | null
  const [openDropdown, setOpenDropdown] = useState(null); // 'state' | 'budget' | 'gender' | null

  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [selectedKidId, setSelectedKidId] = useState(null);

  const categoryRowRef = React.useRef(null);
  const categoriesDivRef = React.useRef(null);

  useSponsorResumeReopen(setSelectedKidId, setShowSponsorModal);

  useEffect(() => {
    getAllKids();
    getAllCategories();
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  let getAllKids = async () => {
    try {
      const { data } = await KidService.fetchKids({
        availableForSponsorship: true,
      });

      const availableKids = filterKidsForSponsorListing(data.data);

      setKids(availableKids);
      let statesArr = availableKids.filter((k) => k.state).map((s) => s.state);
      setStates([...new Set(statesArr)]);
      setFilterSponserKidsData(availableKids);
      setLoading(false);
    } catch (err) {
      console.log("Error fetching kids:", err);
      setLoading(false);
    }
  };

  let getAllCategories = async () => {
    try {
      const { data } = await CategoryService.fetchCategories();
      setCategories(data.data);
    } catch (err) {}
  };

  const onCategory = (e, category_id, index) => {
    if (category_id) {
      setActiveIndex(index);
      setFilterSponserKidsData(
        kids.filter(
          (d) =>
            d.category_id === category_id &&
            (genderVal ? d.gender == genderVal : d.gender) &&
            (stateVal ? d.state == stateVal : d.state) &&
            (budgetVal
              ? parseInt(budgetVal) >= d.total_expenses
              : d.total_expenses) &&
            (searchQuery ? matchesSearch(d, searchQuery) : true),
        ),
      );
      setCategoryId(category_id);
    } else {
      e.target.classList.add("active");
      setActiveIndex(null);
      setFilterSponserKidsData(
        kids.filter((k) =>
          searchQuery ? matchesSearch(k, searchQuery) : true,
        ),
      );
      setCategoryId(null);
    }
  };

  // Helper function to match search query
  const matchesSearch = (kid, query) => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    return (
      kid.name?.toLowerCase().includes(searchTerm) ||
      kid.state?.toLowerCase().includes(searchTerm) ||
      kid.gender?.toLowerCase().includes(searchTerm) ||
      kid.age?.toString().includes(searchTerm) ||
      kid.description?.toLowerCase().includes(searchTerm)
    );
  };

  // Helper function to get the current category name
  const getCurrentCategoryName = () => {
    if (!categoryId) return null;
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : null;
  };

  // Helper function to check if category is empty due to sponsorship
  const isCategoryEmptyDueToSponsorship = () => {
    if (!categoryId) return false;

    // Check if there are kids in this category that are sponsored
    // You'll need to modify this based on your actual data structure
    // This assumes you have access to all kids (including sponsored ones)
    const allKidsInCategory = kids.filter((k) => k.category_id === categoryId);
    return allKidsInCategory.length === 0 && filterSponserKidsData.length === 0;
  };

  // Update the onViewProfile function
  const onViewProfile = (id) => {
    setSelectedKidId(id);
    setShowProfileModal(true);
    setShowSponsorModal(false); // Ensure sponsor modal is closed
  };

  // Update the onSponsor function
  const onSponsor = (id) => {
    setSelectedKidId(id);
    setShowSponsorModal(true);
    setShowProfileModal(false); // Ensure profile modal is closed
  };

  // Update the closeProfileModal function
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedKidId(null);
  };

  // Update the closeSponsorModal function
  const closeSponsorModal = () => {
    setShowSponsorModal(false);
    setSelectedKidId(null);
  };

  const onChangeFilter = (e) => {
    let { value, name, checked } = e.target;
    if (name === "gender") {
      setGenderVal(value);
      applyFilters(value, stateVal, budgetVal, searchQuery);
    } else if (name === "state") {
      setStateVal(value);
      applyFilters(genderVal, value, budgetVal, searchQuery);
    } else if (name === "budget") {
      setBudgetVal(value);
      applyFilters(genderVal, stateVal, value, searchQuery);
    }
  };

  // Handle search input change
  const onSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(genderVal, stateVal, budgetVal, query);
  };

  // Updated helper function to apply filters including search
  const applyFilters = (gender, state, budget, search) => {
    let filteredKids = kids;

    if (gender) {
      filteredKids = filteredKids.filter((k) => k.gender === gender);
    }

    if (state) {
      filteredKids = filteredKids.filter((k) => k.state === state);
    }

    if (budget > 0) {
      filteredKids = filteredKids.filter(
        (k) => parseInt(budget) >= k.total_expenses,
      );
    }

    if (categoryId) {
      filteredKids = filteredKids.filter((k) => k.category_id === categoryId);
    }

    if (search) {
      filteredKids = filteredKids.filter((k) => matchesSearch(k, search));
    }

    setFilterSponserKidsData(filteredKids);
  };

  // Function to render the appropriate empty state message
  const renderEmptyState = () => {
    const categoryName = getCurrentCategoryName();

    if (categoryId && filterSponserKidsData.length === 0) {
      // Check if it's due to filters or if category is actually empty
      const kidsInThisCategory = kids.filter(
        (k) => k.category_id === categoryId,
      );

      if (kidsInThisCategory.length === 0) {
        // Category is empty - all kids are sponsored
        return (
          <div className="col-12 text-center py-5">
            <div
              style={{
                fontSize: "20px",
                color: "#299d8e",
                fontWeight: "600",
                marginBottom: "10px",
              }}
            ></div>
            <div
              style={{
                fontSize: "18px",
                color: "#666",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              All kids in the "{categoryName}" category have already been
              sponsored!
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#999",
                lineHeight: "1.5",
              }}
            >
              Thank you for your generous support. Please check other categories
              to find more children who need your help.
            </div>
          </div>
        );
      } else {
        // There are kids in category but filters are hiding them
        return (
          <div className="col-12 text-center py-5">
            <div
              style={{
                fontSize: "18px",
                color: "#666",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              No fundraisers found in "{categoryName}" matching your current
              filters.
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#999",
                marginTop: "10px",
              }}
            >
              Try adjusting your search terms or filters to see more results.
            </div>
          </div>
        );
      }
    } else if (!categoryId && filterSponserKidsData.length === 0) {
      // General empty state
      return (
        <div className="col-12 text-center py-5">
          <div
            style={{
              fontSize: "18px",
              color: "#666",
              fontWeight: "500",
            }}
          >
            No fundraisers found matching your search criteria.
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#999",
              marginTop: "10px",
            }}
          >
            Try adjusting your filters or search terms.
          </div>
        </div>
      );
    }

    return null;
  };

  // Custom styles remain the same
  const pageStyles = {
    mainContainer: {
      marginTop: "100px",
      overflowX: "hidden",
      maxWidth: "100%",
    },
    profileBgContainer: {
      position: "relative",
      width: "100%",
      maxWidth: "100%",
      marginBottom: "0",
      zIndex: "1",
      overflow: "hidden",
    },
    profileBgImage: {
      width: "100%",
      display: "block",
    },
    contentContainer: {
      position: "relative",
      zIndex: "2",
      backgroundColor: "#fff",
      paddingTop: "20px",
      marginTop: "0",
      overflowX: "hidden",
      maxWidth: "100%",
    },
    categoryContainer: {
      marginTop: "0",
      marginBottom: "20px",
      marginLeft: 0,
      marginRight: 0,
    },
    categoryWrapper: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      maxWidth: "100%",
      overflowX: "hidden",
    },

    filterButton: {
      marginBottom: "15px",
    },
    horizontalFilterContainer: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "15px",
      padding: "15px",
      borderRadius: "5px",
      marginBottom: "20px",
    },
    filterLabel: {
      fontWeight: "500",
      marginRight: "10px",
    },
    filterItem: {
      marginRight: "15px",
    },
    dropdownButton: {
      border: "1px solid #ddd",
      borderRadius: "20px",
      padding: "6px 15px",
      backgroundColor: "#fff",
      fontSize: "14px",
      fontWeight: "500",
    },
    budgetRange: {
      width: "100%",
      maxWidth: "300px",
    },
  };

  return (
    <>
      <>
        {loading ? (
          <Loader />
        ) : (
          <section
            className="category-container"
            style={pageStyles.mainContainer}
          >
            {/* Step 1: Create a dedicated container just for the background image */}
            <div style={pageStyles.profileBgContainer}>
              <img
                src="/img/Sponsor_Shaagird.jpg"
                alt="profile-bg"
                style={pageStyles.profileBgImage}
              />
            </div>

            {/* Step 2: Create a new container with higher z-index for all content */}
            <div className="container" style={pageStyles.contentContainer}>
              {/* Categories row */}
              <div className="row" style={pageStyles.categoryContainer}>
                <div
                  className="col-12 position-relative"
                  style={{
                    ...pageStyles.categoryWrapper,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                >
                  {/* Category list */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Category
                      activeIndex={activeIndex}
                      kidsData={kids}
                      onCategoryHandler={onCategory}
                      categoryData={categories}
                      externalCategoriesDivRef={categoriesDivRef}
                    />
                  </div>
                </div>
              </div>

              {/* Search Bar Section */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="d-flex justify-content-left">
                    <div
                      className="position-relative"
                      style={{ width: "100%", maxWidth: "600px" }}
                    >
                      <div className="position-relative">
                        <FaSearch
                          style={{
                            position: "absolute",
                            left: "20px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#299d8e",
                            fontSize: "18px",
                            zIndex: 2,
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Search by name.."
                          value={searchQuery}
                          onChange={onSearchChange}
                          style={{
                            width: "100%",
                            padding: "16px 20px 16px 55px",
                            border: "2px solid #299d8e",
                            borderRadius: "30px",
                            fontSize: "16px",
                            fontWeight: "400",
                            outline: "none",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 8px rgba(41, 157, 142, 0.1)",
                            transition: "all 0.3s ease",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow =
                              "0 4px 16px rgba(41, 157, 142, 0.2)";
                            e.target.style.borderColor = "#299d8e";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow =
                              "0 2px 8px rgba(41, 157, 142, 0.1)";
                          }}
                        />
                        {searchQuery && (
                          <button
                            onClick={() => {
                              setSearchQuery("");
                              applyFilters(genderVal, stateVal, budgetVal, "");
                            }}
                            style={{
                              position: "absolute",
                              right: "20px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              color: "#999",
                              fontSize: "18px",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: "50%",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#f0f0f0";
                              e.target.style.color = "#666";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "transparent";
                              e.target.style.color = "#999";
                            }}
                          >
                            <VscChromeClose />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Horizontal Filter Bar - Mobile Optimized */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="p-3 rounded">
                    {/* Mobile Layout */}
                    <div className="d-block d-md-none">
                      {/* First Line: Title */}
                      <div className="mb-3 text-center">
                        <span
                          className="fw"
                          style={{ fontSize: 20, fontWeight: 600 }}
                        >
                          Showing Fundraisers For
                        </span>
                      </div>

                      {/* Second Line: Filters */}
                      <div
                        className="d-flex flex-wrap justify-content-center align-items-center"
                        style={{ gap: "10px" }}
                      >
                        {/* Gender Filter */}
                        <div className="position-relative">
                          <button
                            className="filter-dropdown-btn"
                            style={{
                              backgroundColor:
                                openFilter === "gender" ? "#299d8e" : "#fff",
                              color:
                                openFilter === "gender" ? "#fff" : "#299d8e",
                              border: `2px solid #299d8e`,
                              borderRadius: "20px",
                              padding: "0.4rem 1.5rem 0.4rem 1rem",
                              fontWeight: 600,
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              boxShadow:
                                openFilter === "gender"
                                  ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                                  : "none",
                              transition: "all 0.2s",
                            }}
                            type="button"
                            onClick={() =>
                              setOpenFilter(
                                openFilter === "gender" ? null : "gender",
                              )
                            }
                          >
                            Gender {genderVal && `: ${genderVal}`}
                            {openFilter === "gender" ? (
                              <FaChevronUp style={{ marginLeft: 4 }} />
                            ) : (
                              <FaChevronDown style={{ marginLeft: 4 }} />
                            )}
                          </button>
                          {/* Gender dropdown content remains the same */}
                          {openFilter === "gender" && (
                            <div
                              style={{
                                position: "absolute",
                                top: "110%",
                                left: 0,
                                minWidth: 200,
                                background: "#fff",
                                borderRadius: 18,
                                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                                padding: "1.2rem 1.2rem 0.5rem 1.2rem",
                                zIndex: 1000,
                                marginTop: 4,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 16,
                                  marginBottom: 12,
                                  color: "#299d8e",
                                }}
                              >
                                Gender
                              </div>
                              <div
                                style={{
                                  cursor: "pointer",
                                  fontWeight: genderVal === "Male" ? 700 : 500,
                                  color: "#222",
                                  background:
                                    genderVal === "Male"
                                      ? "#FFFBEA"
                                      : "transparent",
                                  borderRadius: 10,
                                  padding: "6px 0 6px 4px",
                                }}
                                onClick={() => {
                                  setGenderVal("Male");
                                  applyFilters(
                                    "Male",
                                    stateVal,
                                    budgetVal,
                                    searchQuery,
                                  );
                                  setOpenFilter(null);
                                }}
                              >
                                Male
                              </div>
                              <div
                                style={{
                                  cursor: "pointer",
                                  fontWeight:
                                    genderVal === "Female" ? 700 : 500,
                                  color: "#222",
                                  background:
                                    genderVal === "Female"
                                      ? "#FFFBEA"
                                      : "transparent",
                                  borderRadius: 10,
                                  padding: "6px 0 6px 4px",
                                }}
                                onClick={() => {
                                  setGenderVal("Female");
                                  applyFilters(
                                    "Female",
                                    stateVal,
                                    budgetVal,
                                    searchQuery,
                                  );
                                  setOpenFilter(null);
                                }}
                              >
                                Female
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  marginTop: 10,
                                }}
                              >
                                <button
                                  style={{
                                    background: "#299d8e",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    padding: "4px 18px",
                                    fontWeight: 600,
                                    fontSize: 15,
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setGenderVal("");
                                    applyFilters(
                                      "",
                                      stateVal,
                                      budgetVal,
                                      searchQuery,
                                    );
                                    setOpenFilter(null);
                                  }}
                                >
                                  Reset
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <span
                          style={{
                            fontSize: 14,
                            color: "#666",
                            fontWeight: 400,
                          }}
                        >
                          from
                        </span>

                        {/* State Filter */}
                        <div className="position-relative">
                          <button
                            className="filter-dropdown-btn"
                            style={{
                              backgroundColor:
                                openDropdown === "state" ? "#299d8e" : "#fff",
                              color:
                                openDropdown === "state" ? "#fff" : "#299d8e",
                              border: `2px solid #299d8e`,
                              borderRadius: "20px",
                              padding: "0.4rem 1.5rem 0.4rem 1rem",
                              fontWeight: 600,
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              boxShadow:
                                openDropdown === "state"
                                  ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                                  : "none",
                              transition: "all 0.2s",
                            }}
                            type="button"
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown === "state" ? null : "state",
                              )
                            }
                          >
                            State{" "}
                            {stateVal &&
                              `: ${stateVal.length > 8 ? stateVal.substring(0, 8) + "..." : stateVal}`}
                            {openDropdown === "state" ? (
                              <FaChevronUp style={{ marginLeft: 4 }} />
                            ) : (
                              <FaChevronDown style={{ marginLeft: 4 }} />
                            )}
                          </button>
                          {/* State dropdown content remains the same */}
                          {openDropdown === "state" && (
                            <div
                              style={{
                                position: "absolute",
                                top: "110%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                minWidth: 200,
                                maxWidth: "90vw",
                                width: "100%",
                                background: "#fff",
                                borderRadius: 18,
                                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                                padding: "1.2rem 1.2rem 0.5rem 1.2rem",
                                zIndex: 1000,
                                marginTop: 4,
                                right: "auto",
                              }}
                            >
                              {states?.length > 0 &&
                                states.map((state) => (
                                  <div
                                    key={state}
                                    style={{
                                      cursor: "pointer",
                                      fontWeight:
                                        stateVal === state ? 700 : 500,
                                      color: "#222",
                                      background:
                                        stateVal === state
                                          ? "#FFFBEA"
                                          : "transparent",
                                      borderRadius: 10,
                                      padding: "6px 0 6px 4px",
                                    }}
                                    onClick={() => {
                                      setStateVal(state);
                                      applyFilters(
                                        genderVal,
                                        state,
                                        budgetVal,
                                        searchQuery,
                                      );
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    {state}
                                  </div>
                                ))}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  marginTop: 10,
                                }}
                              >
                                <button
                                  style={{
                                    background: "#299d8e",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    padding: "4px 18px",
                                    fontWeight: 600,
                                    fontSize: 15,
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setStateVal("");
                                    applyFilters(
                                      genderVal,
                                      "",
                                      budgetVal,
                                      searchQuery,
                                    );
                                    setOpenDropdown(null);
                                  }}
                                >
                                  Reset
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <span
                          style={{
                            fontSize: 14,
                            color: "#666",
                            fontWeight: 400,
                          }}
                        >
                          under
                        </span>

                        {/* Budget Filter */}
                        <div className="position-relative">
                          <button
                            className="filter-dropdown-btn"
                            style={{
                              backgroundColor:
                                openDropdown === "budget" ? "#299d8e" : "#fff",
                              color:
                                openDropdown === "budget" ? "#fff" : "#299d8e",
                              border: `2px solid #299d8e`,
                              borderRadius: "20px",
                              padding: "0.4rem 1.5rem 0.4rem 1rem",
                              fontWeight: 600,
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              boxShadow:
                                openDropdown === "budget"
                                  ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                                  : "none",
                              transition: "all 0.2s",
                            }}
                            type="button"
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown === "budget" ? null : "budget",
                              )
                            }
                          >
                            Monthly Budget {budgetVal > 0 && `: ₹${budgetVal}`}
                            {openDropdown === "budget" ? (
                              <FaChevronUp style={{ marginLeft: 4 }} />
                            ) : (
                              <FaChevronDown style={{ marginLeft: 4 }} />
                            )}
                          </button>
                          {/* Budget dropdown content remains the same */}
                          {openDropdown === "budget" && (
                            <div
                              style={{
                                minWidth: 220,
                                background: "#fff",
                                borderRadius: 18,
                                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                                padding: "1.2rem 1.2rem 0.5rem 1.2rem",
                                zIndex: 1000,
                                marginTop: 4,
                                position: "absolute",
                                top: "110%",
                                left: 0,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 16,
                                  marginBottom: 12,
                                  color: "#222",
                                }}
                              >
                                Monthly Budget
                              </div>
                              <input
                                type="range"
                                min={0}
                                max={5000}
                                value={budgetVal}
                                name="budget"
                                onChange={onChangeFilter}
                                style={{
                                  width: "100%",
                                  accentColor: "#299d8e",
                                  height: 6,
                                  borderRadius: 4,
                                  background: "#299d8e",
                                }}
                              />
                              <div
                                className="d-flex justify-content-between mt-1"
                                style={{ fontWeight: 500, fontSize: 14 }}
                              >
                                <span>₹0</span>
                                <span>₹5000</span>
                              </div>
                              {budgetVal > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    marginTop: 10,
                                  }}
                                >
                                  <button
                                    style={{
                                      background: "#299d8e",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 8,
                                      padding: "4px 18px",
                                      fontWeight: 600,
                                      fontSize: 15,
                                      cursor: "pointer",
                                    }}
                                    onClick={() => {
                                      setBudgetVal(0);
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    Reset
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Reset All Button - Mobile */}
                      {(stateVal ||
                        genderVal ||
                        budgetVal > 0 ||
                        searchQuery) && (
                        <div className="text-center mt-3">
                          <button
                            style={{
                              backgroundColor: "#261638",
                              color: "#fff",
                              border: "none",
                              borderRadius: "20px",
                              padding: "6px 18px",
                              fontWeight: 600,
                              fontSize: 14,
                              cursor: "pointer",
                              transition: "all 0.2s",
                              outline: "none",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#299d8e";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "#261638";
                            }}
                            onClick={() => {
                              setBudgetVal(0);
                              setGenderVal("");
                              setStateVal("");
                              setSearchQuery("");
                              setFilterSponserKidsData(
                                categoryId
                                  ? kids.filter(
                                      (k) => k.category_id === categoryId,
                                    )
                                  : kids,
                              );
                            }}
                          >
                            Reset All
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Desktop Layout - Keep existing code */}
                    <div
                      className="d-none d-md-flex flex-wrap align-items-center"
                      style={{ gap: "18px", alignItems: "center" }}
                    >
                      <span
                        className="fw"
                        style={{
                          fontSize: 24,
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        Showing Fundraisers For
                      </span>
                      {/* Gender Filter */}
                      <div className="me-3 mb-2 position-relative">
                        <button
                          className="filter-dropdown-btn"
                          style={{
                            backgroundColor:
                              openFilter === "gender" ? "#299d8e" : "#fff",
                            color: openFilter === "gender" ? "#fff" : "#299d8e",
                            border: `2px solid #299d8e`,
                            borderRadius: "20px",
                            padding: "0.5rem 2rem 0.5rem 1.5rem",
                            fontWeight: 600,
                            fontSize: 18,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            boxShadow:
                              openFilter === "gender"
                                ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                                : "none",
                            transition: "all 0.2s",
                          }}
                          type="button"
                          onClick={() =>
                            setOpenFilter(
                              openFilter === "gender" ? null : "gender",
                            )
                          }
                        >
                          Gender {genderVal && `: ${genderVal}`}
                          {openFilter === "gender" ? (
                            <FaChevronUp style={{ marginLeft: 8 }} />
                          ) : (
                            <FaChevronDown style={{ marginLeft: 8 }} />
                          )}
                        </button>
                        {openFilter === "gender" && (
                          <div
                            style={{
                              position: "absolute",
                              top: "110%",
                              left: 0,
                              minWidth: 220,
                              background: "#fff",
                              borderRadius: 18,
                              boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                              padding: "1.2rem 1.2rem 0.5rem 1.2rem",
                              zIndex: 1000,
                              marginTop: 4,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 16,
                                marginBottom: 12,
                                color: "#299d8e",
                              }}
                            >
                              Gender
                            </div>
                            <div
                              style={{
                                cursor: "pointer",
                                fontWeight: genderVal === "Male" ? 700 : 500,
                                color: "#222",
                                background:
                                  genderVal === "Male"
                                    ? "#FFFBEA"
                                    : "transparent",
                                borderRadius: 10,
                                padding: "6px 0 6px 4px",
                              }}
                              onClick={() => {
                                setGenderVal("Male");
                                applyFilters(
                                  "Male",
                                  stateVal,
                                  budgetVal,
                                  searchQuery,
                                );
                                setOpenFilter(null);
                              }}
                            >
                              Male
                            </div>
                            <div
                              style={{
                                cursor: "pointer",
                                fontWeight: genderVal === "Female" ? 700 : 500,
                                color: "#222",
                                background:
                                  genderVal === "Female"
                                    ? "#FFFBEA"
                                    : "transparent",
                                borderRadius: 10,
                                padding: "6px 0 6px 4px",
                              }}
                              onClick={() => {
                                setGenderVal("Female");
                                applyFilters(
                                  "Female",
                                  stateVal,
                                  budgetVal,
                                  searchQuery,
                                );
                                setOpenFilter(null);
                              }}
                            >
                              Female
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: 10,
                              }}
                            >
                              <button
                                style={{
                                  background: "#299d8e",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 8,
                                  padding: "4px 18px",
                                  fontWeight: 600,
                                  fontSize: 15,
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setGenderVal("");
                                  applyFilters(
                                    "",
                                    stateVal,
                                    budgetVal,
                                    searchQuery,
                                  );
                                  setOpenFilter(null);
                                }}
                              >
                                Reset
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 22,
                          color: "#222",
                          fontWeight: 400,
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        from
                      </span>
                      <div className="me-3 mb-2 position-relative ">
                        <button
                          className="filter-dropdown-btn"
                          style={{
                            backgroundColor:
                              openDropdown === "state" ? "#299d8e" : "#fff",
                            color:
                              openDropdown === "state" ? "#fff" : "#299d8e",
                            border: `2px solid #299d8e`,
                            borderRadius: "20px",
                            padding: "0.5rem 2rem 0.5rem 1.5rem",
                            fontWeight: 600,
                            fontSize: 18,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            boxShadow:
                              openDropdown === "state"
                                ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                                : "none",
                            transition: "all 0.2s",
                          }}
                          type="button"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "state" ? null : "state",
                            )
                          }
                        >
                          State {stateVal && `: ${stateVal}`}
                          {openDropdown === "state" ? (
                            <FaChevronUp style={{ marginLeft: 8 }} />
                          ) : (
                            <FaChevronDown style={{ marginLeft: 8 }} />
                          )}
                        </button>
                        {openDropdown === "state" && (
                          <div
                            style={{
                              position: "absolute",
                              top: "110%",
                              left: "50%",
                              transform: "translateX(-50%)",
                              minWidth: 220,
                              maxWidth: "90vw",
                              width: "100%",
                              background: "#fff",
                              borderRadius: 18,
                              boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                              padding: "1.2rem 1.2rem 0.5rem 1.2rem",
                              zIndex: 1000,
                              marginTop: 4,
                              right: "auto",
                            }}
                          >
                            {states?.length > 0 &&
                              states.map((state) => (
                                <div
                                  key={state}
                                  style={{
                                    cursor: "pointer",
                                    fontWeight: stateVal === state ? 700 : 500,
                                    color: "#222",
                                    background:
                                      stateVal === state
                                        ? "#FFFBEA"
                                        : "transparent",
                                    borderRadius: 10,
                                    padding: "6px 0 6px 4px",
                                  }}
                                  onClick={() => {
                                    setStateVal(state);
                                    applyFilters(
                                      genderVal,
                                      state,
                                      budgetVal,
                                      searchQuery,
                                    );
                                    setOpenDropdown(null);
                                  }}
                                >
                                  {state}
                                </div>
                              ))}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: 10,
                              }}
                            >
                              <button
                                style={{
                                  background: "#299d8e",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 8,
                                  padding: "4px 18px",
                                  fontWeight: 600,
                                  fontSize: 15,
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setStateVal("");
                                  applyFilters(
                                    genderVal,
                                    "",
                                    budgetVal,
                                    searchQuery,
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                Reset
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <span
                        style={{
                          fontSize: 22,
                          color: "#222",
                          fontWeight: 400,
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        Under
                      </span>

                      <div className="mb-2 position-relative">
                        <button
                          className="filter-dropdown-btn"
                          style={{
                            backgroundColor:
                              openDropdown === "budget" ? "#299d8e" : "#fff",
                            color:
                              openDropdown === "budget" ? "#fff" : "#299d8e",
                            border: `2px solid #299d8e`,
                            borderRadius: "20px",
                            padding: "0.5rem 2rem 0.5rem 1.5rem",
                            fontWeight: 600,
                            fontSize: 18,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            boxShadow:
                              openDropdown === "budget"
                                ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                                : "none",
                            transition: "all 0.2s",
                          }}
                          type="button"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "budget" ? null : "budget",
                            )
                          }
                        >
                          Monthly Budget {budgetVal > 0 && `: ₹${budgetVal}`}
                          {openDropdown === "budget" ? (
                            <FaChevronUp style={{ marginLeft: 8 }} />
                          ) : (
                            <FaChevronDown style={{ marginLeft: 8 }} />
                          )}
                        </button>
                        {openDropdown === "budget" && (
                          <div
                            style={{
                              minWidth: 250,
                              background: "#fff",
                              borderRadius: 18,
                              boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                              padding: "1.2rem 1.2rem 0.5rem 1.2rem",
                              zIndex: 1000,
                              marginTop: 4,
                              position: "absolute",
                              top: "110%",
                              left: 0,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 16,
                                marginBottom: 12,
                                color: "#222",
                              }}
                            >
                              Monthly Budget
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={5000}
                              value={budgetVal}
                              name="budget"
                              onChange={onChangeFilter}
                              style={{
                                width: "100%",
                                accentColor: "#299d8e",
                                height: 6,
                                borderRadius: 4,
                                background: "#299d8e",
                              }}
                            />
                            <div
                              className="d-flex justify-content-between mt-1"
                              style={{ fontWeight: 500, fontSize: 14 }}
                            >
                              <span>₹0</span>
                              <span>₹5000</span>
                            </div>
                            {budgetVal > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  marginTop: 10,
                                }}
                              >
                                <button
                                  style={{
                                    background: "#299d8e",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    padding: "4px 18px",
                                    fontWeight: 600,
                                    fontSize: 15,
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setBudgetVal(0);
                                    setOpenDropdown(null);
                                  }}
                                >
                                  Reset
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Reset All Filters */}
                      {(stateVal ||
                        genderVal ||
                        budgetVal > 0 ||
                        searchQuery) && (
                        <div className="ms-3 mb-2">
                          <button
                            style={{
                              backgroundColor: "#261638",
                              color: "#fff",
                              borderRadius: "20px",
                              padding: "6px 18px",
                              fontWeight: 600,
                              fontSize: 14,
                              cursor: "pointer",
                              transition: "all 0.2s",
                              outline: "none",
                              border: "none",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#299d8e";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "#261638";
                            }}
                            onClick={() => {
                              setBudgetVal(0);
                              setGenderVal("");
                              setStateVal("");
                              setSearchQuery("");
                              setFilterSponserKidsData(
                                categoryId
                                  ? kids.filter(
                                      (k) => k.category_id === categoryId,
                                    )
                                  : kids,
                              );
                            }}
                          >
                            Reset All
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content row */}
              <div className="custom-card-grid" style={{ marginTop: "1rem" }}>
                {filterSponserKidsData?.length > 0
                  ? filterSponserKidsData.map((data) => (
                      <div className="custom-card-col" key={data.id}>
                        <Card
                          onViewProfile={onViewProfile}
                          onSponsor={onSponsor}
                          data={data}
                        />
                      </div>
                    ))
                  : renderEmptyState()}
              </div>
            </div>
          </section>
        )}

        {/* Kid Profile Modal */}
        <KidProfileModal
          isOpen={showProfileModal}
          onClose={closeProfileModal}
          kidId={selectedKidId}
          onSponsorClick={(id) => {
            closeProfileModal();
            onSponsor(id);
          }}
        />

        {/* Kid Sponsor Modal */}
        <KidSponsorModal
          isOpen={showSponsorModal}
          onClose={closeSponsorModal}
          kidId={selectedKidId}
        />
      </>

      <style>{`
        .custom-card-grid {
          display: flex;
          flex-wrap: wrap;
          margin-left: 0;
          margin-right: 0;
          width: 100%;
        }
        .custom-card-col {
          padding: 12px;
          flex: 0 0 25%;
          max-width: 25%;
          display: flex;
          justify-content: center;
        }
        @media (max-width: 1270px) {
          .custom-card-col {
            flex: 0 0 33.3333%;
            max-width: 33.3333%;
          }
        }
        @media (max-width: 991px) {
          .custom-card-col {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }
        @media (max-width: 768px) {
          .custom-card-col {
            flex: 0 0 100%;
            max-width: 100%;
          }
        }
        
        @media (max-width: 768px) {
          .card {
            width: 100%;
            max-width: 100%;
            margin: 0 0 1.5rem 0;
            height: 432px;
          }
        }

        @media (max-width: 480px) {
          .card {
            margin: 0 0 1rem 0;
          }
        }

        @media (min-width: 769px) and (max-width: 991px) {
          .card {
            max-width: 280px;
          }
        }
      `}</style>
    </>
  );
};

export default SponsorKids;
